import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from './client'
import { mockParents, mockTrees, mockUnmatched } from './portfolios.mock'

// --- Types ---

export type PortfolioRole = 'auto' | 'broad' | 'exact' | 'pat' | 'migration_source'

export interface PortfolioCampaign {
  id: number
  name: string
  adType: 'sp' | 'sb' | 'sd'
  state: 'enabled' | 'paused' | 'archived'
  role: PortfolioRole
  spend30d: number | null
  acos30d: number | null
  orders30d: number | null
  lastNewTermAt?: string | null
}

export interface PortfolioParent {
  id: number
  amazonParentAsin: string
  name: string
  ppcCode?: string | null
  childrenCount: number
  campaignsCount: number
  spend30d: number | null
  acos30d: number | null
  orders30d: number | null
}

export interface PortfolioChildNode {
  amazonChildAsin: string
  autoCampaigns: PortfolioCampaign[]
}

export interface PortfolioTree {
  parent: PortfolioParent
  portfolioCampaigns: PortfolioCampaign[]
  children: PortfolioChildNode[]
  legacyCampaigns: PortfolioCampaign[]
}

export interface PortfolioUnmatchedCampaign {
  id: number
  name: string
  adType: 'sp' | 'sb' | 'sd'
  state: 'enabled' | 'paused' | 'archived'
  spend30d: number | null
  acos30d: number | null
  orders30d: number | null
}

export interface UpdateMappingPayload {
  childAsin: string
  newParentId: number
  reason?: string
}

export interface UpdateCampaignAssignmentPayload {
  campaignId: number
  parentId: number
  role: PortfolioRole
}

// --- Portfolio-Builder (Phase f) ---

export type BuilderRole = 'broad' | 'exact' | 'pat' | 'auto'

export interface BuilderKeyword {
  text: string
  matchType?: 'broad' | 'phrase' | 'exact'
  bid?: number
}

export interface BuilderPatTarget {
  asin: string
  bid?: number
}

export interface PortfolioBuilderPayload {
  parentId: number
  role: BuilderRole
  adType: 'sp' | 'sb' | 'sd'
  budget: number
  biddingStrategy?: 'dynamic_bids_down_only' | 'dynamic_bids_up_and_down' | 'fixed_bid'
  childAsins: string[]
  seedKeywords?: BuilderKeyword[]
  patTargets?: BuilderPatTarget[]
  parentShortOverride?: string
  startBidDefault?: number
}

export interface PortfolioBuilderResult {
  draftId: number
  groupId: string
  generatedName: string
  previewPayload: {
    campaign: { name: string; adType: string; budget: number; biddingStrategy?: string }
    adGroupName: string
    childAsins: string[]
    keywords: Array<{ text: string; matchType: string; bid: number }>
    patTargets: Array<{ asin: string; bid: number }>
  }
  warnings: string[]
}

export interface PortfolioDraft {
  id: number
  parentId: number
  role: BuilderRole
  generatedName: string
  config: Record<string, unknown>
  state: 'draft' | 'queued' | 'applied' | 'failed' | 'cancelled'
  createdBy: number | null
  createdAt: string
  updatedAt: string
}

// --- Mock-Fallback-Strategie ---
// Wenn Backend-Endpoint einen 404 / NetworkError liefert, Mock-Daten verwenden
// und global "mockActive"-Flag setzen (für Info-Leiste).
const MOCK_FORCED =
  typeof import.meta !== 'undefined' &&
  (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_USE_PORTFOLIO_MOCK === 'true'

let mockDetectedAtRuntime = false

export function isPortfolioMockActive(): boolean {
  return MOCK_FORCED || mockDetectedAtRuntime
}

function shouldFallbackToMock(err: unknown): boolean {
  if (err instanceof ApiError) {
    // 404 = Endpoint noch nicht deployed, 501 = Not Implemented
    if (err.status === 404 || err.status === 501) return true
  }
  // NetworkError (fetch wirft TypeError) — Backend offline
  if (err instanceof TypeError) return true
  return false
}

async function withMockFallback<T>(real: () => Promise<T>, mock: () => T): Promise<T> {
  if (MOCK_FORCED) return mock()
  try {
    return await real()
  } catch (err) {
    if (shouldFallbackToMock(err)) {
      mockDetectedAtRuntime = true
      return mock()
    }
    throw err
  }
}

// --- Hooks ---

export function useParents() {
  return useQuery({
    queryKey: ['portfolios', 'parents'],
    queryFn: () =>
      withMockFallback(
        () => api.get<{ parents: PortfolioParent[] }>('/portfolios/parents/').then((r) => r.parents),
        () => mockParents,
      ),
    staleTime: 60_000,
  })
}

export function useParentTree(parentId: number | null) {
  return useQuery({
    queryKey: ['portfolios', 'tree', parentId],
    queryFn: () =>
      withMockFallback(
        () => api.get<PortfolioTree>(`/portfolios/tree/?parentId=${parentId}`),
        () => {
          const t = parentId != null ? mockTrees[parentId] : undefined
          if (!t) throw new Error('Parent nicht gefunden (mock)')
          return t
        },
      ),
    enabled: parentId !== null,
    staleTime: 60_000,
  })
}

export function useUnmatchedCampaigns() {
  return useQuery({
    queryKey: ['portfolios', 'unmatched'],
    queryFn: () =>
      withMockFallback(
        () =>
          api
            .get<{ campaigns: PortfolioUnmatchedCampaign[] }>('/portfolios/unmatched/')
            .then((r) => r.campaigns),
        () => mockUnmatched,
      ),
    staleTime: 60_000,
  })
}

export function useUpdateMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateMappingPayload) => {
      const { childAsin, ...body } = payload
      return withMockFallback(
        () => api.put(`/portfolios/mapping/${encodeURIComponent(childAsin)}/`, body),
        () => ({ ack: 1 }),
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolios'] })
    },
  })
}

export function useUpdateCampaignAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: UpdateCampaignAssignmentPayload) => {
      const { campaignId, ...body } = payload
      return withMockFallback(
        () => api.put(`/campaigns/${campaignId}/portfolio-assignment/`, body),
        () => ({ ack: 1 }),
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolios'] })
    },
  })
}

// --- Portfolio-Builder (Phase f) ---

export function useCreatePortfolioDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: PortfolioBuilderPayload) => {
      return withMockFallback<{ draft: PortfolioBuilderResult }>(
        () => api.post<{ draft: PortfolioBuilderResult }>('/portfolios/drafts/', payload),
        () => {
          // Mock: simuliertes Ergebnis mit lokaler Namens-Ableitung
          const parent = mockParents.find((p) => p.id === payload.parentId)
          const parentShort = (parent?.name ?? 'UNKNOWN')
            .toUpperCase()
            .replace(/[^A-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .slice(0, 40)
          const ppc = parent?.ppcCode ? `${parent.ppcCode}_` : ''
          const generatedName = `${ppc}${parentShort}_${payload.role.toUpperCase()}`
          const warnings: string[] = []
          if ((payload.seedKeywords?.length ?? 0) > 5) {
            warnings.push(
              `Mina-Empfehlung: max. 5 Keywords pro Kampagne — Spend konzentriert sich sonst auf Top-Keywords (${payload.seedKeywords!.length} gewaehlt)`,
            )
          }
          return {
            draft: {
              draftId: Math.floor(Math.random() * 10000),
              groupId: crypto.randomUUID(),
              generatedName,
              previewPayload: {
                campaign: {
                  name: generatedName,
                  adType: payload.adType,
                  budget: payload.budget,
                  ...(payload.biddingStrategy ? { biddingStrategy: payload.biddingStrategy } : {}),
                },
                adGroupName: `${parentShort}_${payload.role.toUpperCase()}_AG1`,
                childAsins: payload.childAsins,
                keywords: (payload.seedKeywords ?? []).map((k) => ({
                  text: k.text,
                  matchType: k.matchType ?? (payload.role === 'broad' ? 'broad' : 'exact'),
                  bid: k.bid ?? 0.3,
                })),
                patTargets: (payload.patTargets ?? []).map((t) => ({
                  asin: t.asin,
                  bid: t.bid ?? 0.3,
                })),
              },
              warnings,
            },
          }
        },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['portfolios'] })
    },
  })
}
